import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Guide } from '../../../types/guide';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  guides: Guide[];
  selectedGuideId: string;
  onSelectGuide: (guideId: string) => void;
  userRole: string;
  userId?: string;
};

export const GuideSearch = ({
  guides,
  selectedGuideId,
  onSelectGuide,
  userRole,
  userId,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGuides, setFilteredGuides] = useState<Guide[]>([]);
  const { translations } = useLanguage();

  useEffect(() => {
    const filtered = Object.values(guides)
      .filter((guide) => {
        // Filter by role and search term
        const matchesRole =
          userRole === 'admin' || guide.organizerId === userId;
        const matchesSearch =
          !searchTerm ||
          guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guide.email.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesRole && matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    setFilteredGuides(filtered);
  }, [guides, searchTerm, userRole, userId]);

  return (
    <div className="space-y-2">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {translations?.guideSearch?.label || 'Guide'}
        <span className="text-red-500 mr-1">
          {translations?.guideSearch?.requiredMark || '*'}
        </span>
      </label>

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={
            translations?.guideSearch?.placeholder ||
            'Search by name or email...'
          }
          className="w-full px-4 py-3 pl-10 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
        />
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={20}
        />
      </div>

      {/* Guides List */}
      <div className="border rounded-lg max-h-48 overflow-y-auto">
        {filteredGuides.length > 0 ? (
          filteredGuides.map((guide) => (
            <button
              key={guide.id}
              onClick={() => onSelectGuide(guide.id)}
              className={`w-full p-3 text-right hover:bg-gray-50 transition-colors border-b last:border-b-0 
                ${selectedGuideId === guide.id ? 'bg-primary-50' : ''}`}
            >
              <div className="font-medium">{guide.name}</div>
              <div className="text-sm text-gray-500">{guide.email}</div>
            </button>
          ))
        ) : (
          <div className="p-4 text-center text-gray-500">
            {searchTerm
              ? translations?.guideSearch?.noMatchingGuides ||
                'No matching guides found'
              : translations?.guideSearch?.noGuidesFound ||
                'No available guides'}
          </div>
        )}
      </div>
    </div>
  );
};